import { useState, useEffect, useMemo, FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FileText } from 'lucide-react'
import { api } from '../lib/api'
import { Button } from '@/components/ui/button'
import {
  Field, FieldGroup, FieldLabel, FieldSet,
} from '@/components/ui/field'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import { extractErr } from '@/components/ui/inline-edit'
import { blobErr } from '../lib/blobErr'
import { LanguagePicker } from '@/components/LanguagePicker'
import { ISO639_BY_CODE } from '../lib/iso639'

type Category = {
  category: string
  label: string
  source_type: string
  schema: string[]
}

type Subsidiary = {
  id: number
  code: string
  name: string
}

type TemplateLite = {
  id: number
  category: string
  language_code: string
  active: boolean
}

interface Props {
  /** Django "app.Model" id, e.g. "hr.Employment" or "consulting.ConsultantProfile". */
  sourceType: string
  /** Primary key of the source record. */
  sourceId: number
  /** Optional override for the button label. */
  label?: string
  /** Called after a successful generation so parent pages can refresh their generated-doc list. */
  onGenerated?: () => void
}

export default function GenerateDocumentButton({
  sourceType, sourceId, label = 'Generate document…', onGenerated,
}: Props) {
  const [open, setOpen] = useState(false)

  const categoriesQ = useQuery({
    queryKey: ['document-templates', 'categories'],
    queryFn: async () =>
      (await api.get<Category[]>('/document-templates/categories/')).data,
    enabled: open, // only fetch when modal opens
  })

  const subsidiariesQ = useQuery({
    queryKey: ['subsidiaries'],
    queryFn: async () => {
      const r = await api.get('/subsidiaries/')
      const body = r.data
      if (Array.isArray(body)) return body as Subsidiary[]
      if (body && Array.isArray(body.results)) return body.results as Subsidiary[]
      return [] as Subsidiary[]
    },
    enabled: open,
  })

  // Active templates — used to derive the available-language list per category.
  const templatesQ = useQuery({
    queryKey: ['document-templates'],
    queryFn: async () => {
      const r = await api.get('/document-templates/')
      const body = r.data
      const arr = Array.isArray(body) ? body : Array.isArray(body?.results) ? body.results : []
      return (arr as TemplateLite[]).filter((t) => t.active)
    },
    enabled: open,
  })

  const cats = (categoriesQ.data || []).filter((c) => c.source_type === sourceType)
  const subs = subsidiariesQ.data || []
  const tpls = templatesQ.data || []

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <FileText className="h-4 w-4 mr-1.5" />
        {label}
      </Button>
      {open && (
        <GenerateDialog
          sourceType={sourceType}
          sourceId={sourceId}
          categories={cats}
          subsidiaries={subs}
          templates={tpls}
          onClose={(generated) => {
            setOpen(false)
            if (generated && onGenerated) onGenerated()
          }}
        />
      )}
    </>
  )
}

function GenerateDialog({
  sourceType, sourceId, categories, subsidiaries, templates, onClose,
}: {
  sourceType: string
  sourceId: number
  categories: Category[]
  subsidiaries: Subsidiary[]
  templates: TemplateLite[]
  onClose: (generated: boolean) => void
}) {
  const [category, setCategory] = useState<string | undefined>(undefined)
  const [subsidiary, setSubsidiary] = useState<string>('__none__')
  const [languageCode, setLanguageCode] = useState('')
  const [format, setFormat] = useState<'pdf' | 'docx'>('pdf')
  const [submitting, setSubmitting] = useState(false)

  const noCategories = categories.length === 0
  const singleCategory = categories.length === 1
  const singleSubsidiary = subsidiaries.length <= 1

  // Auto-pick when there's exactly one option for each axis.
  useEffect(() => {
    if (singleCategory && !category) setCategory(categories[0].category)
  }, [singleCategory, category, categories])

  // Languages with at least one active template for the selected category.
  // Splits blank ("any") from concrete ISO codes so we can render them
  // differently in the picker.
  const {
    availableCodes,    // ISO codes, lowercase, e.g. ['en', 'de']
    hasBlankTemplate,  // true if any active template has language_code = ''
  } = useMemo(() => {
    if (!category) return { availableCodes: [] as string[], hasBlankTemplate: false }
    const forCat = templates.filter((t) => t.category === category)
    const set = new Set<string>()
    let blank = false
    for (const t of forCat) {
      const lc = (t.language_code || '').toLowerCase()
      if (lc) set.add(lc)
      else blank = true
    }
    // Sort: known names alphabetically, unknown codes at the end.
    const codes = Array.from(set).sort((a, b) => {
      const an = ISO639_BY_CODE[a]?.name || a
      const bn = ISO639_BY_CODE[b]?.name || b
      return an.localeCompare(bn)
    })
    return { availableCodes: codes, hasBlankTemplate: blank }
  }, [category, templates])

  const singleLanguage = availableCodes.length === 1 && !hasBlankTemplate
  const noLanguageChoice =
    availableCodes.length === 0 && hasBlankTemplate // only blank → no concrete choice

  // Default the language each time the category changes:
  //   1. 'en' if available
  //   2. else the first available concrete code
  //   3. else blank (any language)
  useEffect(() => {
    if (!category) { setLanguageCode(''); return }
    if (availableCodes.includes('en')) setLanguageCode('en')
    else if (availableCodes.length > 0) setLanguageCode(availableCodes[0])
    else setLanguageCode('')
  }, [category, availableCodes])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!category) return
    setSubmitting(true)
    try {
      const payload: any = {
        category,
        source_id: sourceId,
        language_code: languageCode.trim().toLowerCase(),
        as: format,
      }
      if (subsidiary && subsidiary !== '__none__') payload.subsidiary = Number(subsidiary)

      const res = await api.post('/documents/render/', payload, { responseType: 'blob' })
      const blob = new Blob([res.data], {
        type: format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      })
      // Pull the filename from Content-Disposition when present.
      const cd = res.headers['content-disposition'] || ''
      const m = /filename="([^"]+)"/.exec(cd)
      const filename = m ? m[1] : `${category}.${format}`
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
      window.URL.revokeObjectURL(link.href)
      toast.success(`Generated ${filename}`)
      onClose(true)
    } catch (err) {
      toast.error(await blobErr(err), { duration: 10000 })
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40">
      <div className="bg-card rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-1">Generate document</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Renders a template for <span className="font-mono">{sourceType}#{sourceId}</span> via the configured subsidiary/language fallback chain.
        </p>

        {noCategories ? (
          <div className="text-sm text-muted-foreground italic mb-4">
            No template categories bind to <span className="font-mono">{sourceType}</span>.
            Create one under Setup → Document Templates.
          </div>
        ) : (
          <form onSubmit={submit}>
            <FieldSet>
              <FieldGroup>
                {/* Category — collapse to a chip when there's only one. */}
                {singleCategory ? (
                  <Field>
                    <FieldLabel>Document type</FieldLabel>
                    <div className="inline-flex items-center text-sm bg-muted/40 rounded px-2 py-1 w-fit">
                      {categories[0].label}
                    </div>
                  </Field>
                ) : (
                  <Field>
                    <FieldLabel>Category</FieldLabel>
                    <SearchableSelect
                      value={category}
                      onChange={(v) => setCategory(v)}
                      placeholder="Pick a document type…"
                      options={categories.map((c) => ({ value: c.category, label: c.label }))}
                    />
                  </Field>
                )}
                <div className={singleSubsidiary ? '' : 'grid grid-cols-2 gap-3'}>
                  {!singleSubsidiary && (
                    <Field>
                      <FieldLabel>Subsidiary</FieldLabel>
                      <SearchableSelect
                        value={subsidiary}
                        onChange={(v) => setSubsidiary(v)}
                        placeholder="Auto"
                        options={[
                          { value: '__none__', label: 'Global / auto' },
                          // Name first, code as the muted hint — same shape the
                          // template admin uses for its own subsidiary picker.
                          // The search box matches on hint too, so typing the
                          // code still finds the row.
                          ...subsidiaries.map((s) => ({
                            value: String(s.id), label: s.name || s.code, hint: s.code,
                          })),
                        ]}
                      />
                    </Field>
                  )}
                  <Field>
                    <FieldLabel>Language</FieldLabel>
                    {!category ? (
                      <div className="inline-flex items-center text-sm text-muted-foreground italic h-10 px-1">
                        Pick a category first…
                      </div>
                    ) : noLanguageChoice ? (
                      <div className="inline-flex items-center text-sm bg-muted/40 rounded px-2 py-1 w-fit">
                        Any (language-neutral template)
                      </div>
                    ) : singleLanguage ? (
                      <div className="inline-flex items-center text-sm bg-muted/40 rounded px-2 py-1 w-fit">
                        <span className="font-mono text-xs text-muted-foreground mr-2">
                          {availableCodes[0].toUpperCase()}
                        </span>
                        {ISO639_BY_CODE[availableCodes[0]]?.name || availableCodes[0].toUpperCase()}
                      </div>
                    ) : (
                      <LanguagePicker
                        value={languageCode}
                        onChange={(code) => setLanguageCode(code)}
                        allowedCodes={availableCodes}
                        includeBlank={hasBlankTemplate}
                      />
                    )}
                  </Field>
                </div>
                <Field>
                  <FieldLabel>Format</FieldLabel>
                  <Select value={format} onValueChange={(v) => setFormat(v as 'pdf' | 'docx')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="docx">DOCX</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </FieldGroup>
            </FieldSet>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="ghost" onClick={() => onClose(false)}>Cancel</Button>
              <Button type="submit" disabled={!category || submitting}>
                {submitting ? 'Generating…' : 'Generate & download'}
              </Button>
            </div>
          </form>
        )}

        {noCategories && (
          <div className="flex justify-end pt-2">
            <Button type="button" variant="ghost" onClick={() => onClose(false)}>Close</Button>
          </div>
        )}
      </div>
    </div>
  )
}
